import React, { useState } from 'react';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import { debtorService } from '../../services/debtorService';
import { useNotification } from '../../hooks/useNotification';

const AddDebtorModal = ({ isOpen, onClose, onSuccess }) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    initialDebt: '',
    paymentPlan: 'none',
    notes: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        paymentPlan: formData.paymentPlan,
        notes: formData.notes
      };

      // Only add initialDebt if it's provided and > 0
      if (formData.initialDebt && parseFloat(formData.initialDebt) > 0) {
        dataToSend.initialDebt = parseFloat(formData.initialDebt);
      }

      await debtorService.create(dataToSend);
      showSuccess('Debtor added successfully!');

      // Reset form
      setFormData({
        name: '',
        mobile: '',
        email: '',
        initialDebt: '',
        paymentPlan: 'none',
        notes: ''
      });

      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error adding debtor:', error);
      showError(error.message || 'Failed to add debtor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      mobile: '',
      email: '',
      initialDebt: '',
      paymentPlan: 'none',
      notes: ''
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Debtor">
      <form onSubmit={handleSubmit}>
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Debtor's name"
          required
        />

        <Input
          label="Mobile Number"
          name="mobile"
          value={formData.mobile}
          onChange={handleChange}
          placeholder="+973 XXXXXXXX"
          required
        />

        <Input
          label="Email (Optional)"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="debtor@example.com"
        />

        <Input
          label="Initial Debt Amount (BHD)"
          type="number"
          step="0.001"
          name="initialDebt"
          value={formData.initialDebt}
          onChange={handleChange}
          placeholder="0.000"
        />

        <div className="mb-4">
          <label htmlFor="paymentPlan" className="block text-sm font-medium text-gray-700 mb-1">
            Payment Plan
          </label>
          <select
            id="paymentPlan"
            name="paymentPlan"
            value={formData.paymentPlan}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="none">No Payment Plan</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Bi-weekly</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes (Optional)
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Add any notes about this debtor..."
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            Add Debtor
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddDebtorModal;
